import { Address } from 'viem';

export const DEFAULT_BRIDGE_INPUT_DECIMALS = 6; // decimals to show in input

export const enum OftTypes {
  Proxy, // uses outboundAmount, does not mint or burn
  Indirect, // mints and burns as owner in erc20
  OftV2, // actual token
}

export interface BridgeToken {
  enabled: boolean;
  symbol: string;
  decimals: number;
  sharedDecimals: number;
  address: Record<number, Address>;
  imageUrl?: string;
  oftAddress: Record<number, Address>;
  oftType: Record<number, OftTypes>;
  enableBridge: Record<number, boolean>;
  enabledChains: number[];
}

export const bridgeTokens: BridgeToken[] = [
  {
    enabled: true,
    symbol: 'SFI',
    address: {
      1: '0x7636D8722Fdf7cd34232a915E48e96aA3eB386BF',
      56: '0x7636D8722Fdf7cd34232a915E48e96aA3eB386BF',
      8453: '0x863B7364a29daA23C9FCBD02e27D129A8B185891',
    },
    oftAddress: {
      1: '0x14B4106B754954387c48BcA8E7E8E003c9356e53',
      56: '0x87c2Fe15DeFe49FFdE6DD389bdD9C7238F72d766',
      8453: '0xEbfa0339C9C4373b8A6E5B9Ae6b3A11b2558A089',
    },
    oftType: {
      1: OftTypes.Proxy,
      56: OftTypes.Indirect,
      8453: OftTypes.Indirect,
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/SFI.png`,
    decimals: 18,
    sharedDecimals: 10,
    enableBridge: { 1: true, 56: true, 8453: true },
    enabledChains: [1, 56, 8453],
  },
  {
    enabled: true,
    symbol: 'SFI',
    address: {
      31438: '0x7636D8722Fdf7cd34232a915E48e96aA3eB386BF',
      31439: '0x7636D8722Fdf7cd34232a915E48e96aA3eB386BF',
    },
    oftAddress: {
      31438: '0x14B4106B754954387c48BcA8E7E8E003c9356e53',
      31439: '0x87c2Fe15DeFe49FFdE6DD389bdD9C7238F72d766',
    },
    oftType: {
      31438: OftTypes.Proxy,
      31439: OftTypes.Indirect,
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/SFI.png`,
    decimals: 18,
    sharedDecimals: 10,
    enableBridge: { 31438: true, 31439: true },
    enabledChains: [31438, 31439],
  },
  {
    enabled: true,
    symbol: 'SOPH',
    address: {
      1: '0x73fBD93bFDa83B111DdC092aa3a4ca77fD30d380', // original token on mainnet
      56: '0x73fBD93bFDa83B111DdC092aa3a4ca77fD30d380', // original token on bsc
      8453: '0x73fBD93bFDa83B111DdC092aa3a4ca77fD30d380', // base l2
    },
    oftAddress: {
      1: '0xf2a3300Eb0574951E893EbFf15089Edb38FcFA3E', // proxy oft v2 with reverse message
      56: '0x0E446FAad63e344167489b5Cf1111F4CC305CBd3', // indirect oft v2
      8453: '0x17de46760F4c18C26eEc36117C23793299F564A8', // base l2
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/SOPH.svg`,
    oftType: {
      1: OftTypes.Proxy,
      56: OftTypes.Indirect,
      8453: OftTypes.Indirect,
    },
    decimals: 18,
    sharedDecimals: 10,
    enableBridge: { 1: true, 56: true, 8453: true },
    enabledChains: [1, 56, 8453],
  },
  {
    enabled: true,
    symbol: 'SOPH',
    address: {
      31438: '0x73fBD93bFDa83B111DdC092aa3a4ca77fD30d380', // forked eth
      31439: '0x73fBD93bFDa83B111DdC092aa3a4ca77fD30d380', // forked bnb
    },
    oftAddress: {
      31438: '0xf2a3300Eb0574951E893EbFf15089Edb38FcFA3E', // forked eth
      31439: '0x0E446FAad63e344167489b5Cf1111F4CC305CBd3', // forked bnb
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/SOPH.svg`,
    oftType: {
      31438: OftTypes.Proxy,
      31439: OftTypes.Indirect,
    },
    decimals: 18,
    sharedDecimals: 10,
    enableBridge: { 31438: true, 31439: true },
    enabledChains: [31438, 31439],
  },
  {
    enabled: true,
    symbol: 'RJV',
    address: {
      1: '0xa1f410f13B6007fCa76833ee7eb58478D47Bc5Ef', // original token on mainnet
      56: '0x602B6C6ccE5F95c00603bd07D8fA7EbAF3747D44', // original token on bsc
    },
    oftAddress: {
      1: '0xd8ba3a437aE3e95A9be47dC2bf46a05723DeED89', // indirect oft v2
      56: '0x335095094b8BF3d435dd7Bde6298eBd8499f2665', // indirect oft v2
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/RJV.svg`,
    oftType: { 1: OftTypes.Indirect, 56: OftTypes.Indirect },
    decimals: 6,
    sharedDecimals: 6,
    enableBridge: { 1: true, 56: true },
    enabledChains: [1, 56],
  },
  {
    enabled: true,
    symbol: 'RJV',
    address: {
      31438: '0xa1f410f13B6007fCa76833ee7eb58478D47Bc5Ef', // original token on mainnet
      31439: '0x602B6C6ccE5F95c00603bd07D8fA7EbAF3747D44', // original token on bsc
    },
    oftAddress: {
      31438: '0xd8ba3a437aE3e95A9be47dC2bf46a05723DeED89', // indirect oft v2
      31439: '0x335095094b8BF3d435dd7Bde6298eBd8499f2665', // indirect oft v2
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/RJV.svg`,
    oftType: { 31438: OftTypes.Indirect, 31439: OftTypes.Indirect },
    decimals: 6,
    sharedDecimals: 6,
    enableBridge: { 31438: true, 31439: true },
    enabledChains: [31438, 31439],
  },
  {
    enabled: true,
    symbol: 'SKYA',
    address: {
      1: '0x623cD3a3EdF080057892aaF8D773Bbb7A5C9b6e9',
      8453: '0x623cD3a3EdF080057892aaF8D773Bbb7A5C9b6e9',
    },
    oftAddress: {
      1: '0x1b89Cb4e324Af944383d70074527C6751CaED01B',
      8453: '0x90Ed8F1dc86388f14b64ba8fb4bbd23099f18240',
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/SKYA.svg`,
    oftType: { 1: OftTypes.Indirect, 8453: OftTypes.Indirect },
    decimals: 18,
    sharedDecimals: 10,
    enableBridge: { 1: true, 8453: true },
    enabledChains: [1, 8453],
  },
  {
    enabled: true,
    symbol: 'HYPC',
    address: {
      1: '0xeA7B7DC089c9a4A916B5a7a37617f59fD54e37E4', // original token on mainnet
      56: '0x7881cD2B5724431372F57C50E91611352557A606', // original token on bsc
    },
    oftAddress: {
      1: '0x7df1b50eD82aCea5a5200E0D74ab96086b216D32', // proxy oft v2 with reverse message
      56: '0xA06c743b8032AA0c9D137b7E9A00653a3F46d6A3', // proxy oft v2 with reverse message
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/HYPC.svg`,
    oftType: { 1: OftTypes.Proxy, 56: OftTypes.Proxy },
    decimals: 6,
    sharedDecimals: 6,
    enableBridge: { 1: true, 56: true },
    enabledChains: [1, 56],
  },
  {
    enabled: true,
    symbol: 'HYPC',
    address: {
      31438: '0xeA7B7DC089c9a4A916B5a7a37617f59fD54e37E4', // original token on mainnet
      31439: '0x7881cD2B5724431372F57C50E91611352557A606', // original token on bsc
    },
    oftAddress: {
      31438: '0x7df1b50eD82aCea5a5200E0D74ab96086b216D32', // proxy oft v2 with reverse message
      31439: '0xA06c743b8032AA0c9D137b7E9A00653a3F46d6A3', // proxy oft v2 with reverse message
    },
    imageUrl: `${process.env.NEXT_PUBLIC_STATIC_ASSETS_BUCKET}/sfi/icons/tokens/HYPC.svg`,
    oftType: { 31438: OftTypes.Proxy, 31439: OftTypes.Proxy },
    decimals: 6,
    sharedDecimals: 6,
    enableBridge: { 31438: true, 31439: true },
    enabledChains: [31438, 31439],
  },
];
